
import { useState } from 'react';
import { Comment } from '@/types/social';
import { ContentType } from '@/services/comments/types';
import { toast } from 'sonner';
import { useCollectionRealtime } from '../realtime/useCollectionRealtime';
import { 
  handleNewCommentEvent,
  handleCommentUpdateEvent,
  handleCommentDeleteEvent
} from '@/services/comments/realtime/commentEventHandlers';

interface UseCommentsRealtimeProps {
  contentId: string;
  contentType: ContentType;
  parentId?: string | null;
  onNewComment?: (comment: Comment) => void;
  onDeleteComment?: (commentId: string) => void;
  onCountChange?: (newCount: number) => void;
}

/**
 * Hook for subscribing to real-time comment updates using Universal Real-time Manager
 */
export const useCommentsRealtime = ({
  contentId,
  contentType,
  parentId = null,
  onNewComment,
  onDeleteComment,
  onCountChange
}: UseCommentsRealtimeProps) => {
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Set up filters based on content type and parent
  const filters = {
    ...(contentType === 'poll' ? { pollId: contentId } : { cardId: contentId }),
    ...(parentId && { parentId })
  };

  const { isConnected, connectionStatus } = useCollectionRealtime(
    'comments',
    filters,
    {
      onItemAdded: async (payload) => {
        console.log(`Received realtime comment insert:`, payload);
        
        await handleNewCommentEvent(
          { new: payload, eventType: 'INSERT' },
          contentId,
          contentType,
          parentId,
          onNewComment,
          onCountChange
        );
      },
      
      onItemUpdated: async (payload) => {
        console.log(`Received realtime comment update:`, payload);
        
        await handleCommentUpdateEvent(
          { new: payload, eventType: 'UPDATE' },
          contentId,
          contentType,
          onDeleteComment,
          onCountChange
        );
      },
      
      onItemRemoved: async (itemId) => {
        console.log(`Received realtime comment delete:`, itemId);
        
        await handleCommentDeleteEvent(
          { old: { id: itemId }, eventType: 'DELETE' },
          contentId,
          contentType,
          onDeleteComment,
          onCountChange
        );
      },
      
      onError: (error) => {
        console.error('Comments real-time error:', error);
        toast.error('Failed to connect to comment updates');
      }
    },
    !!contentId // Only enabled if contentId is provided
  );

  // Update subscription status when connection changes
  useState(() => {
    setIsSubscribed(isConnected);
  });

  return {
    isSubscribed: isConnected
  };
};
